import { useState, useEffect, useMemo } from 'react'
import {
  X,
  Building2,
  FileText,
  CreditCard,
  Star,
  Phone,
  MessageCircle,
  Mail,
  Pencil,
  Trash2,
  AlertTriangle,
} from 'lucide-react'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { PageSpinner } from '@/components/ui/Spinner'
import {
  useBuyers,
  useSaveBuyer,
  useDeleteBuyer,
  fetchBuyerAssociations,
  useInvoices,
  useBuyerInvoices,
  useRecordBatchPayment,
  useBuyerContacts,
  useSaveBuyerContact,
  useDeleteBuyerContact,
  useStoreSupplyItems,
  useBuyerProducts,
  useSaveBuyerProduct,
  useDeleteBuyerProduct,
} from '@/hooks/useBuyers'
import type { BuyerAssociations } from '@/hooks/useBuyers'
import { useAuthStore } from '@/stores/authStore'
import type { Buyer, BuyerContact, Invoice, InvoiceStatus, PaymentTerms } from '@/types/app'

function computeInvoiceBalance(invoice: Invoice): number {
  const subtotal = (invoice.line_items ?? []).reduce(
    (sum, item) => sum + item.qty * item.unit_price,
    0
  )
  const grandTotal =
    subtotal * (1 + (invoice.tax_percent ?? 0) / 100) - (invoice.discount_amount ?? 0)
  return Math.max(0, grandTotal - (invoice.amount_paid ?? 0))
}


function fmt(amount: number): string {
  return amount.toLocaleString('en-US', { style: 'currency', currency: 'USD' })
}

function today(): string {
  return new Date().toISOString().split('T')[0]
}

const STATUS_BADGE: Record<InvoiceStatus, { label: string; classes: string }> = {
  draft:     { label: 'Draft',     classes: 'bg-stone-100 text-stone-500' },
  sent:      { label: 'Sent',      classes: 'bg-blue-100 text-blue-700' },
  partial:   { label: 'Partial',   classes: 'bg-amber-100 text-amber-700' },
  paid:      { label: 'Paid',      classes: 'bg-green-100 text-green-700' },
  overdue:   { label: 'Overdue',   classes: 'bg-red-100 text-red-700' },
  cancelled: { label: 'Cancelled', classes: 'bg-stone-100 text-stone-400' },
}

const PAYMENT_TERMS_OPTIONS: PaymentTerms[] = [
  'COD',
  'Due on Receipt',
  'Net 15',
  'Net 30',
  'Net 45',
  'Net 60',
  'Net EOM',
]

const PAYMENT_TERMS_LABELS: Record<PaymentTerms, string> = {
  'COD':            'COD',
  'Due on Receipt': 'Due on Receipt',
  'Net 15':         'Net 15',
  'Net 30':         'Net 30',
  'Net 45':         'Net 45',
  'Net 60':         'Net 60',
  'Net EOM':        'Net EOM — End of Month',
}

interface PanelForm {
  id?: string
  buyer_code: string
  city_code: string
  name: string
  city: string
  default_payment_terms: PaymentTerms
  contact_name: string
  phone: string
  email: string
  address: string
  is_active: boolean
}

function emptyForm(): PanelForm {
  return {
    buyer_code: '',
    city_code: '',
    name: '',
    city: '',
    default_payment_terms: 'Net 30',
    contact_name: '',
    phone: '',
    email: '',
    address: '',
    is_active: true,
  }
}

interface ContactFormState {
  id?: string
  name: string
  role: string
  phone: string
  whatsapp: string
  email: string
  is_primary: boolean
}

function emptyContactForm(): ContactFormState {
  return {
    name: '',
    role: '',
    phone: '',
    whatsapp: '',
    email: '',
    is_primary: false,
  }
}

function ContactsSection({ buyerId }: { buyerId: string }) {
  const { data: contacts, isLoading } = useBuyerContacts(buyerId)
  const saveContact = useSaveBuyerContact()
  const deleteContact = useDeleteBuyerContact()

  const [editingContact, setEditingContact] = useState<ContactFormState | null>(null)
  const [contactError, setContactError] = useState<string | null>(null)

  function startAdd() {
    setContactError(null)
    setEditingContact(emptyContactForm())
  }

  function startEdit(c: BuyerContact) {
    setContactError(null)
    setEditingContact({
      id: c.id,
      name: c.name,
      role: c.role ?? '',
      phone: c.phone ?? '',
      whatsapp: c.whatsapp ?? '',
      email: c.email ?? '',
      is_primary: c.is_primary,
    })
  }

  function cancelEdit() {
    setEditingContact(null)
    setContactError(null)
  }

  async function handleSetPrimary(c: BuyerContact) {
    try {
      await saveContact.mutateAsync({
        id: c.id,
        buyer_id: buyerId,
        name: c.name,
        role: c.role ?? undefined,
        phone: c.phone ?? undefined,
        whatsapp: c.whatsapp ?? undefined,
        email: c.email ?? undefined,
        is_primary: true,
      })
    } catch {
      // silent — not critical
    }
  }

  async function handleDelete(c: BuyerContact) {
    try {
      await deleteContact.mutateAsync({ id: c.id, buyerId })
    } catch {
      // silent
    }
  }

  async function handleSaveContact() {
    if (!editingContact) return
    if (!editingContact.name.trim()) {
      setContactError('Name is required')
      return
    }
    setContactError(null)
    try {
      await saveContact.mutateAsync({
        ...(editingContact.id ? { id: editingContact.id } : {}),
        buyer_id: buyerId,
        name: editingContact.name.trim(),
        role: editingContact.role.trim() || undefined,
        phone: editingContact.phone.trim() || undefined,
        whatsapp: editingContact.whatsapp.trim() || undefined,
        email: editingContact.email.trim() || undefined,
        is_primary: editingContact.is_primary,
      })
      setEditingContact(null)
    } catch (e) {
      setContactError(e instanceof Error ? e.message : 'Failed to save contact')
    }
  }

  const inputCls =
    'flex h-9 w-full rounded border border-stone-300 bg-white px-3 py-2 text-sm text-stone-900 placeholder:text-stone-400 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-brand-500'

  return (
    <div className="border-t border-stone-200 pt-4 space-y-3">
      <div className="flex items-center justify-between">
        <p className="text-sm font-semibold text-stone-700">Contacts</p>
        {!editingContact && (
          <button
            type="button"
            onClick={startAdd}
            className="text-xs font-medium text-brand-600 hover:text-brand-700"
          >
            + Add Contact
          </button>
        )}
      </div>

      {isLoading && <p className="text-xs text-stone-400">Loading contacts...</p>}

      {!isLoading && (contacts ?? []).length === 0 && !editingContact && (
        <p className="text-xs text-stone-400">No contacts yet.</p>
      )}

      {(contacts ?? []).map(c => (
        <div
          key={c.id}
          className="flex items-start gap-2 p-2.5 rounded-lg border border-stone-100 bg-stone-50"
        >
          <button
            type="button"
            onClick={() => handleSetPrimary(c)}
            className="mt-0.5 shrink-0 text-stone-300 hover:text-amber-500 transition-colors"
            title={c.is_primary ? 'Primary contact' : 'Set as primary'}
          >
            {c.is_primary ? (
              <Star size={14} className="fill-amber-400 text-amber-400" />
            ) : (
              <Star size={14} />
            )}
          </button>

          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-sm font-semibold text-stone-900">{c.name}</span>
              {c.role && (
                <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs bg-stone-200 text-stone-600">
                  {c.role}
                </span>
              )}
            </div>
            <div className="flex flex-wrap gap-x-3 gap-y-0.5 mt-1">
              {c.phone && (
                <span className="flex items-center gap-1 text-xs text-stone-500">
                  <Phone size={11} />
                  {c.phone}
                </span>
              )}
              {c.whatsapp && (
                <span className="flex items-center gap-1 text-xs text-stone-500">
                  <MessageCircle size={11} />
                  {c.whatsapp}
                </span>
              )}
              {c.email && (
                <span className="flex items-center gap-1 text-xs text-stone-500">
                  <Mail size={11} />
                  {c.email}
                </span>
              )}
            </div>
          </div>

          <div className="flex items-center gap-1 shrink-0">
            <button
              type="button"
              onClick={() => startEdit(c)}
              className="p-1 text-stone-400 hover:text-stone-700 transition-colors"
              title="Edit"
            >
              <Pencil size={13} />
            </button>
            <button
              type="button"
              onClick={() => handleDelete(c)}
              className="p-1 text-stone-400 hover:text-red-500 transition-colors"
              title="Delete"
            >
              <Trash2 size={13} />
            </button>
          </div>
        </div>
      ))}

      {editingContact && (
        <div className="rounded-lg border border-stone-200 p-3 space-y-3 bg-white">
          {contactError && <p className="text-xs text-red-600">{contactError}</p>}

          <div className="grid grid-cols-2 gap-3">
            <div className="flex flex-col gap-1">
              <label className="text-xs font-medium text-stone-700">
                Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={editingContact.name}
                onChange={e =>
                  setEditingContact(f => f ? { ...f, name: e.target.value } : f)
                }
                placeholder="Full name"
                className={inputCls}
              />
            </div>
            <div className="flex flex-col gap-1">
              <label className="text-xs font-medium text-stone-700">Role</label>
              <input
                type="text"
                value={editingContact.role}
                onChange={e =>
                  setEditingContact(f => f ? { ...f, role: e.target.value } : f)
                }
                placeholder="e.g. Manager, Procurement"
                className={inputCls}
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="flex flex-col gap-1">
              <label className="text-xs font-medium text-stone-700">Phone</label>
              <input
                type="tel"
                value={editingContact.phone}
                onChange={e =>
                  setEditingContact(f => f ? { ...f, phone: e.target.value } : f)
                }
                placeholder="555-000-1234"
                className={inputCls}
              />
            </div>
            <div className="flex flex-col gap-1">
              <label className="text-xs font-medium text-stone-700">WhatsApp</label>
              <input
                type="tel"
                value={editingContact.whatsapp}
                onChange={e =>
                  setEditingContact(f => f ? { ...f, whatsapp: e.target.value } : f)
                }
                placeholder="555-000-1234"
                className={inputCls}
              />
            </div>
          </div>

          <div className="flex flex-col gap-1">
            <label className="text-xs font-medium text-stone-700">Email</label>
            <input
              type="email"
              value={editingContact.email}
              onChange={e =>
                setEditingContact(f => f ? { ...f, email: e.target.value } : f)
              }
              placeholder="contact@example.com"
              className={inputCls}
            />
          </div>

          <label className="flex items-center gap-2 text-xs text-stone-700 cursor-pointer">
            <input
              type="checkbox"
              checked={editingContact.is_primary}
              onChange={e =>
                setEditingContact(f => f ? { ...f, is_primary: e.target.checked } : f)
              }
              className="rounded border-stone-300 text-brand-500 focus:ring-brand-500"
            />
            Primary contact
          </label>

          <div className="flex gap-2">
            <Button
              size="sm"
              onClick={handleSaveContact}
              loading={saveContact.isPending}
            >
              Save Contact
            </Button>
            <Button size="sm" variant="outline" onClick={cancelEdit}>
              Cancel
            </Button>
          </div>
        </div>
      )}
    </div>
  )
}

// ── Contract products ──────────────────────────────────────────────────────
// The set of products this buyer is actually contracted to buy. Mark products
// here and they become what invoicing offers for this buyer — manually and via
// RezzAI — instead of the full catalog. (Kept separate from any future
// "best sellers" feature, which will be about sales volume, not contracts.)
function BuyerProductsSection({ buyerId }: { buyerId: string }) {
  const { data: contractProducts, isLoading } = useBuyerProducts(buyerId)
  const { data: allProducts = [] } = useStoreSupplyItems()
  const saveProduct = useSaveBuyerProduct()
  const deleteProduct = useDeleteBuyerProduct()

  const [search, setSearch] = useState('')
  const [priceEditId, setPriceEditId] = useState<string | null>(null)
  const [priceDraft, setPriceDraft] = useState('')

  const contractedIds = new Set((contractProducts ?? []).map(f => f.menu_item_id))
  const q = search.trim().toLowerCase()
  const available = allProducts
    .filter(p => !contractedIds.has(p.id))
    .filter(p => !q || p.name.toLowerCase().includes(q))

  async function handleAdd(menuItemId: string) {
    await saveProduct.mutateAsync({ buyer_id: buyerId, menu_item_id: menuItemId })
  }

  async function handleRemove(id: string) {
    await deleteProduct.mutateAsync({ id, buyerId })
  }

  function startPriceEdit(id: string, currentPrice: number | null) {
    setPriceEditId(id)
    setPriceDraft(currentPrice != null ? String(currentPrice) : '')
  }

  async function savePriceEdit(menuItemId: string) {
    const value = priceDraft.trim() === '' ? null : parseFloat(priceDraft)
    setPriceEditId(null)
    await saveProduct.mutateAsync({ buyer_id: buyerId, menu_item_id: menuItemId, contract_price: value })
  }

  const inputCls =
    'flex h-9 w-full rounded border border-stone-300 bg-white px-3 py-2 text-sm text-stone-900 placeholder:text-stone-400 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-brand-500'

  return (
    <div className="border-t border-stone-200 pt-4 space-y-3">
      <div>
        <p className="text-sm font-semibold text-stone-700">Contract Products</p>
        <p className="text-xs text-stone-400 mt-0.5">
          Click a product below to mark it as contracted for this buyer. Once set, invoicing this buyer — including
          RezzAI — only offers these instead of the full catalog.
        </p>
      </div>

      {/* Currently contracted, as removable chips */}
      <div>
        <p className="text-2xs font-semibold uppercase tracking-wide text-stone-400 mb-1.5">
          Contracted{contractProducts?.length ? ` (${contractProducts.length})` : ''}
        </p>
        {isLoading ? (
          <p className="text-xs text-stone-400">Loading...</p>
        ) : (contractProducts ?? []).length === 0 ? (
          <p className="text-xs text-stone-400">None marked yet — invoicing will show the full product list.</p>
        ) : (
          <div className="flex flex-wrap gap-1.5">
            {contractProducts!.map(f => (
              <div
                key={f.id}
                className="flex items-center gap-1.5 pl-2.5 pr-1.5 py-1 rounded-full bg-brand-50 border border-brand-200 text-xs text-brand-800"
              >
                <span className="font-medium">{f.menu_item?.name ?? 'Unknown product'}</span>
                {priceEditId === f.id ? (
                  <input
                    autoFocus
                    type="text"
                    inputMode="decimal"
                    value={priceDraft}
                    onChange={e => setPriceDraft(e.target.value)}
                    onBlur={() => savePriceEdit(f.menu_item_id)}
                    onKeyDown={e => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur() }}
                    placeholder={f.menu_item?.wholesale_price != null ? String(f.menu_item.wholesale_price) : '$'}
                    className="w-14 h-5 text-2xs text-right rounded border border-brand-300 px-1 bg-white focus:outline-none"
                  />
                ) : (
                  <button
                    type="button"
                    onClick={() => startPriceEdit(f.id, f.contract_price)}
                    className="flex items-center gap-0.5 text-brand-500 hover:text-brand-700"
                    title="Set a contract price (optional) — otherwise the catalog price is used"
                  >
                    {f.contract_price != null ? `$${f.contract_price.toFixed(2)}` : <Pencil size={10} />}
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => handleRemove(f.id)}
                  className="text-brand-400 hover:text-red-500 transition-colors"
                  title="Remove"
                >
                  <X size={12} />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Click to add — every remaining product shown as a chip, filterable */}
      <div>
        <p className="text-2xs font-semibold uppercase tracking-wide text-stone-400 mb-1.5">Add products</p>
        <input
          type="text"
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder="Search products…"
          className={inputCls}
        />
        <div className="mt-2 flex flex-wrap gap-1.5 max-h-44 overflow-y-auto">
          {available.length === 0 ? (
            <p className="text-xs text-stone-400">
              {allProducts.length === 0
                ? 'No products are marked available for store supply yet — add that in Products first.'
                : q ? 'No matching products.' : 'Every product is already marked as contracted.'}
            </p>
          ) : available.map(p => (
            <button
              key={p.id}
              type="button"
              onClick={() => handleAdd(p.id)}
              className="px-2.5 py-1 rounded-full border border-stone-300 bg-white text-xs text-stone-600 hover:border-brand-400 hover:bg-brand-50 hover:text-brand-700 transition-colors"
            >
              + {p.name}
            </button>
          ))}
        </div>
      </div>
    </div>
  )
}

// ── Delete confirmation modal ─────────────────────────────────────────────────
function DeleteBuyerModal({
  buyer,
  onClose,
  onDeleted,
}: {
  buyer: Buyer
  onClose: () => void
  onDeleted: () => void
}) {
  const deleteBuyer = useDeleteBuyer()
  const [assoc, setAssoc] = useState<BuyerAssociations | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    fetchBuyerAssociations(buyer.id).then(a => { setAssoc(a); setLoading(false) })
  }, [buyer.id])

  const hasInvoices = (assoc?.invoices.length ?? 0) > 0
  const prefix = (buyer.buyer_code ?? '') + (buyer.city_code ?? '??')

  async function handleDelete() {
    await deleteBuyer.mutateAsync(buyer.id)
    onDeleted()
  }

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-md overflow-hidden">
        <div className="flex items-center gap-3 px-5 py-4 border-b border-stone-200">
          <div className="w-9 h-9 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0">
            <AlertTriangle size={18} className="text-danger" />
          </div>
          <div>
            <h3 className="font-semibold text-stone-900">Delete Buyer</h3>
            <p className="text-xs text-stone-500">{buyer.name}</p>
          </div>
          <button onClick={onClose} className="ml-auto text-stone-400 hover:text-stone-600">
            <X size={16} />
          </button>
        </div>

        <div className="px-5 py-4 space-y-3">
          {loading ? (
            <p className="text-sm text-stone-400 py-4 text-center">Checking associations…</p>
          ) : (
            <>
              {/* Invoice block — always shown */}
              <div className={[
                'rounded-lg border px-4 py-3',
                hasInvoices ? 'border-red-200 bg-red-50' : 'border-stone-100 bg-stone-50',
              ].join(' ')}>
                <p className={['text-sm font-medium mb-1', hasInvoices ? 'text-red-800' : 'text-stone-600'].join(' ')}>
                  {hasInvoices ? '⛔ Has invoices — cannot delete' : '✓ No invoices'}
                </p>
                {hasInvoices && (
                  <ul className="mt-1 space-y-0.5">
                    {assoc!.invoices.map(inv => (
                      <li key={inv.id} className="text-xs text-red-700 font-mono">
                        {inv.invoice_number} <span className="font-sans text-red-500">({inv.status})</span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              {/* Contacts */}
              {(assoc!.contacts.length > 0) && (
                <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3">
                  <p className="text-sm font-medium text-amber-800 mb-1">
                    ⚠ {assoc!.contacts.length} contact{assoc!.contacts.length > 1 ? 's' : ''} will be removed
                  </p>
                  <ul className="space-y-0.5">
                    {assoc!.contacts.map(c => (
                      <li key={c.id} className="text-xs text-amber-700">{c.name}</li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Invoice prefix callout */}
              <p className="text-xs text-stone-500">
                Invoice prefix <span className="font-mono font-semibold text-stone-700">{prefix}</span> and all
                associated sequence data will be permanently removed.
              </p>

              {!hasInvoices && (
                <p className="text-sm text-stone-600">
                  This action cannot be undone. <strong>{buyer.name}</strong> will be permanently deleted.
                </p>
              )}
            </>
          )}
        </div>

        <div className="px-5 py-3 border-t border-stone-200 bg-stone-50 flex justify-between items-center">
          <button onClick={onClose} className="text-sm text-stone-500 hover:text-stone-700">Cancel</button>
          {!loading && !hasInvoices && (
            <Button
              variant="danger"
              size="sm"
              onClick={handleDelete}
              loading={deleteBuyer.isPending}
            >
              <Trash2 size={13} />
              Delete Permanently
            </Button>
          )}
        </div>
      </div>
    </div>
  )
}

function BuyerPanel({
  buyer,
  onClose,
}: {
  buyer: Buyer | null
  onClose: () => void
}) {
  const saveBuyer = useSaveBuyer()
  const { data: allBuyers = [] } = useBuyers()
  const [showDelete, setShowDelete] = useState(false)
  const [form, setForm] = useState<PanelForm>(() =>
    buyer
      ? {
          id: buyer.id,
          buyer_code: buyer.buyer_code ?? '',
          city_code: buyer.city_code ?? '',
          name: buyer.name,
          city: buyer.city ?? '',
          default_payment_terms: (buyer.default_payment_terms as PaymentTerms) ?? 'Net 30',
          contact_name: buyer.contact_name ?? '',
          phone: buyer.phone ?? '',
          email: buyer.email ?? '',
          address: buyer.address ?? '',
          is_active: buyer.is_active,
        }
      : emptyForm()
  )
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (buyer) {
      setForm({
        id: buyer.id,
        buyer_code: buyer.buyer_code ?? '',
        city_code: buyer.city_code ?? '',
        name: buyer.name,
        city: buyer.city ?? '',
        default_payment_terms: (buyer.default_payment_terms as PaymentTerms) ?? 'Net 30',
        contact_name: buyer.contact_name ?? '',
        phone: buyer.phone ?? '',
        email: buyer.email ?? '',
        address: buyer.address ?? '',
        is_active: buyer.is_active,
      })
    } else {
      setForm(emptyForm())
    }
    setError(null)
  }, [buyer?.id]) // eslint-disable-line react-hooks/exhaustive-deps

  // Live uniqueness check — ignore the buyer currently being edited
  const bc = form.buyer_code.trim().toUpperCase()
  const cc = form.city_code.trim().toUpperCase()
  const prefixConflict = bc.length === 2 && cc.length === 2
    ? allBuyers.find(b =>
        b.id !== form.id &&
        b.buyer_code?.toUpperCase() === bc &&
        b.city_code?.toUpperCase() === cc
      )
    : null

  async function handleSave() {
    if (!bc) { setError('Buyer code is required'); return }
    if (bc.length !== 2) { setError('Buyer code must be exactly 2 letters'); return }
    if (!cc) { setError('City code is required'); return }
    if (cc.length !== 2) { setError('City code must be exactly 2 letters'); return }
    if (prefixConflict) { setError(`Prefix ${bc}${cc} is already used by "${prefixConflict.name}"`); return }
    if (!form.name.trim()) { setError('Name is required'); return }
    setError(null)
    try {
      await saveBuyer.mutateAsync({
        ...(form.id ? { id: form.id } : {}),
        buyer_code: bc,
        city_code: cc,
        name: form.name.trim(),
        city: form.city.trim() || null,
        default_payment_terms: form.default_payment_terms,
        contact_name: form.contact_name.trim() || null,
        phone: form.phone.trim() || null,
        email: form.email.trim() || null,
        address: form.address.trim() || null,
        is_active: form.is_active,
      })
      onClose()
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to save')
    }
  }

  const isNew = !form.id

  return (
    <>
      <div className="fixed inset-0 bg-black/20 z-40" onClick={onClose} />
      <div className="fixed right-0 top-0 h-full w-full max-w-md bg-white shadow-modal z-50 flex flex-col">
        <div className="flex items-center justify-between px-5 py-4 border-b border-stone-200">
          <h2 className="text-base font-semibold text-stone-900">
            {isNew ? 'Add Buyer' : 'Edit Buyer'}
          </h2>
          <div className="flex items-center gap-2">
            {!isNew && buyer && (
              <button
                type="button"
                onClick={() => setShowDelete(true)}
                className="p-1.5 rounded hover:bg-red-50 text-stone-400 hover:text-danger transition-colors"
                title="Delete buyer"
              >
                <Trash2 size={15} />
              </button>
            )}
            <button
              type="button"
              onClick={onClose}
              className="text-stone-400 hover:text-stone-600"
            >
              <X size={18} />
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
          {error && <p className="text-sm text-danger">{error}</p>}

          <Input
            label="Name"
            required
            value={form.name}
            onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
            placeholder="e.g. Sunrise Foods – Alpharetta"
          />

          {/* Invoice prefix: buyer code + city code */}
          <div className="flex flex-col gap-2">
            <label className="text-sm font-medium text-stone-700">
              Invoice Code <span className="text-danger">*</span>
            </label>
            <div className="flex items-start gap-2">
              <div className="flex flex-col gap-1 flex-1">
                <input
                  type="text"
                  maxLength={2}
                  value={form.buyer_code}
                  onChange={e =>
                    setForm(f => ({
                      ...f,
                      buyer_code: e.target.value.toUpperCase().replace(/[^A-Z]/g, ''),
                    }))
                  }
                  placeholder="SU"
                  className="flex h-9 w-full rounded border border-stone-300 bg-white px-3 py-2 text-sm font-mono font-bold text-center text-stone-900 uppercase placeholder:font-sans placeholder:font-normal placeholder:text-stone-400 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
                />
                <p className="text-xs text-stone-400 text-center">Buyer (2 letters)</p>
              </div>
              <div className="flex flex-col gap-1 flex-1">
                <input
                  type="text"
                  maxLength={2}
                  value={form.city_code}
                  onChange={e =>
                    setForm(f => ({
                      ...f,
                      city_code: e.target.value.toUpperCase().replace(/[^A-Z]/g, ''),
                    }))
                  }
                  placeholder="AL"
                  className="flex h-9 w-full rounded border border-stone-300 bg-white px-3 py-2 text-sm font-mono font-bold text-center text-stone-900 uppercase placeholder:font-sans placeholder:font-normal placeholder:text-stone-400 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
                />
                <p className="text-xs text-stone-400 text-center">City (2 letters)</p>
              </div>
              {/* Live preview */}
              <div className={[
                'flex flex-col gap-1 items-center justify-center px-3 py-2 rounded border border-dashed min-w-[120px]',
                prefixConflict ? 'border-red-300 bg-red-50' : 'border-stone-300 bg-stone-50',
              ].join(' ')}>
                <span className={[
                  'font-mono font-bold text-sm tracking-wider',
                  prefixConflict ? 'text-danger' : 'text-stone-900',
                ].join(' ')}>
                  {(form.buyer_code || '??') + (form.city_code || '??')}-MMYY-01
                </span>
                <span className="text-2xs text-stone-400">invoice preview</span>
              </div>
            </div>

            {/* Conflict warning */}
            {prefixConflict && (
              <p className="text-xs font-medium text-danger flex items-center gap-1">
                <AlertTriangle size={12} />
                <span className="font-mono font-bold">{bc}{cc}</span> is already used by &ldquo;{prefixConflict.name}&rdquo; — choose a different code.
              </p>
            )}

            {!prefixConflict && (
              <p className="text-xs text-stone-400">
                Each store needs a unique 4-letter prefix. E.g. SU (Suvidha) + AL (Alpharetta) → <span className="font-mono font-medium text-stone-600">SUAL</span>
              </p>
            )}
          </div>

          <div className="flex flex-col gap-1.5">
            <label className="text-sm font-medium text-stone-700">City</label>
            <input
              type="text"
              value={form.city}
              onChange={e => setForm(f => ({ ...f, city: e.target.value }))}
              placeholder="e.g. Alpharetta"
              className="flex h-9 w-full rounded border border-stone-300 bg-white px-3 py-2 text-sm text-stone-900 placeholder:text-stone-400 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
            />
          </div>

          <div className="flex flex-col gap-1.5">
            <label className="text-sm font-medium text-stone-700">Default Payment Terms</label>
            <select
              value={form.default_payment_terms}
              onChange={e =>
                setForm(f => ({ ...f, default_payment_terms: e.target.value as PaymentTerms }))
              }
              className="flex h-9 w-full rounded border border-stone-300 bg-white px-3 py-2 text-sm text-stone-900 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
            >
              {PAYMENT_TERMS_OPTIONS.map(t => (
                <option key={t} value={t}>{PAYMENT_TERMS_LABELS[t]}</option>
              ))}
            </select>
          </div>

          <Input
            label="Contact Name"
            value={form.contact_name}
            onChange={e => setForm(f => ({ ...f, contact_name: e.target.value }))}
            placeholder="e.g. Ravi Kumar"
          />

          <Input
            label="Phone"
            type="tel"
            value={form.phone}
            onChange={e => setForm(f => ({ ...f, phone: e.target.value }))}
            placeholder="e.g. 555-000-1234"
          />

          <Input
            label="Email"
            type="email"
            value={form.email}
            onChange={e => setForm(f => ({ ...f, email: e.target.value }))}
            placeholder="e.g. orders@sunrisefoods.com"
          />

          <div className="flex flex-col gap-1.5">
            <label className="text-sm font-medium text-stone-700">Address</label>
            <textarea
              rows={2}
              value={form.address}
              onChange={e => setForm(f => ({ ...f, address: e.target.value }))}
              placeholder="Street, City, State ZIP"
              className="flex w-full rounded border border-stone-300 bg-white px-3 py-2 text-sm text-stone-900 placeholder:text-stone-400 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-brand-500 resize-none"
            />
          </div>

          <label className="flex items-center gap-2 text-sm text-stone-700 cursor-pointer">
            <input
              type="checkbox"
              checked={form.is_active}
              onChange={e => setForm(f => ({ ...f, is_active: e.target.checked }))}
              className="rounded border-stone-300 text-brand-500 focus:ring-brand-500"
            />
            Active buyer
          </label>

          {form.id && <ContactsSection buyerId={form.id} />}
          {form.id && <BuyerProductsSection buyerId={form.id} />}
        </div>

        <div className="px-5 py-4 border-t border-stone-200">
          <Button
            className="w-full"
            onClick={handleSave}
            loading={saveBuyer.isPending}
            disabled={!!prefixConflict}
          >
            {isNew ? 'Add Buyer' : 'Save Changes'}
          </Button>
        </div>
      </div>

      {showDelete && buyer && (
        <DeleteBuyerModal
          buyer={buyer}
          onClose={() => setShowDelete(false)}
          onDeleted={onClose}
        />
      )}
    </>
  )
}

function RecordPaymentPanel({
  buyer,
  onClose,
}: {
  buyer: Buyer
  onClose: () => void
}) {
  const role = useAuthStore(s => s.role)
  const { data: invoices, isLoading } = useBuyerInvoices(buyer.id)
  const recordPayment = useRecordBatchPayment()

  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set())
  const [paymentAmount, setPaymentAmount] = useState('')
  const [paymentDate, setPaymentDate] = useState(today())
  const [paymentMethod, setPaymentMethod] = useState('Bank Transfer')
  const [notes, setNotes] = useState('')
  const [successMsg, setSuccessMsg] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  const canRecord = role === 'owner' || role === 'ops'

  useEffect(() => {
    if (invoices) {
      setSelectedIds(new Set(invoices.map(inv => inv.id)))
    }
  }, [invoices])

  const pending = invoices ?? []

  const selectedTotal = useMemo(() => {
    return pending
      .filter(inv => selectedIds.has(inv.id))
      .reduce((sum, inv) => sum + computeInvoiceBalance(inv), 0)
  }, [pending, selectedIds])

  function toggleInvoice(id: string) {
    setSelectedIds(prev => {
      const next = new Set(prev)
      if (next.has(id)) {
        next.delete(id)
      } else {
        next.add(id)
      }
      return next
    })
  }

  function selectAll() {
    setSelectedIds(new Set(pending.map(inv => inv.id)))
  }

  function selectNone() {
    setSelectedIds(new Set())
  }

  async function handleApply() {
    const amount = parseFloat(paymentAmount)
    if (!paymentAmount || isNaN(amount) || amount <= 0) {
      setError('Enter a valid payment amount')
      return
    }
    if (selectedIds.size === 0) {
      setError('Select at least one invoice')
      return
    }
    setError(null)
    try {
      const result = await recordPayment.mutateAsync({
        buyerId: buyer.id,
        paymentAmount: amount,
        paymentDate,
        notes: notes
          ? `[${paymentMethod}] ${notes}`
          : `[${paymentMethod}]`,
      })
      setSuccessMsg(`Payment recorded. ${result.updatedCount} invoice${result.updatedCount !== 1 ? 's' : ''} updated.`)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to record payment')
    }
  }

  return (
    <>
      <div className="fixed inset-0 bg-black/20 z-40" onClick={onClose} />
      <div className="fixed right-0 top-0 h-full w-full max-w-lg bg-white shadow-modal z-50 flex flex-col">
        <div className="flex items-center justify-between px-5 py-4 border-b border-stone-200">
          <h2 className="text-base font-semibold text-stone-900">
            Record Payment — {buyer.name}
          </h2>
          <button type="button" onClick={onClose} className="text-stone-400 hover:text-stone-600">
            <X size={18} />
          </button>
        </div>

        {successMsg ? (
          <div className="flex-1 flex flex-col items-center justify-center px-5 gap-4">
            <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center">
              <CreditCard size={22} className="text-green-600" />
            </div>
            <p className="text-base font-medium text-stone-900 text-center">{successMsg}</p>
            <Button variant="outline" size="sm" onClick={onClose}>
              Close
            </Button>
          </div>
        ) : (
          <>
            <div className="flex-1 overflow-y-auto px-5 py-4 space-y-5">
              {error && <p className="text-sm text-danger">{error}</p>}

              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <p className="text-sm font-semibold text-stone-700">Pending Invoices</p>
                  {pending.length > 1 && (
                    <div className="flex gap-2 text-xs text-stone-500">
                      <button type="button" onClick={selectAll} className="hover:text-brand-600">
                        All
                      </button>
                      <span>·</span>
                      <button type="button" onClick={selectNone} className="hover:text-brand-600">
                        None
                      </button>
                    </div>
                  )}
                </div>

                {isLoading && (
                  <p className="text-sm text-stone-400">Loading invoices...</p>
                )}

                {!isLoading && pending.length === 0 && (
                  <p className="text-sm text-stone-400">No pending invoices for this buyer.</p>
                )}

                {pending.map(inv => {
                  const balance = computeInvoiceBalance(inv)
                  const badge = STATUS_BADGE[inv.status] ?? STATUS_BADGE.draft
                  return (
                    <label
                      key={inv.id}
                      className="flex items-start gap-3 p-3 rounded-lg border border-stone-200 hover:bg-stone-50 cursor-pointer"
                    >
                      <input
                        type="checkbox"
                        checked={selectedIds.has(inv.id)}
                        onChange={() => toggleInvoice(inv.id)}
                        disabled={!canRecord}
                        className="mt-0.5 rounded border-stone-300 text-brand-500 focus:ring-brand-500"
                      />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between gap-2">
                          <span className="font-mono text-xs font-semibold text-stone-700">
                            {inv.invoice_number}
                          </span>
                          <span
                            className={`inline-flex items-center px-1.5 py-0.5 rounded text-xs font-semibold ${badge.classes}`}
                          >
                            {badge.label}
                          </span>
                        </div>
                        <div className="flex items-center justify-between mt-1">
                          <span className="text-xs text-stone-400">
                            {new Date(inv.issued_date).toLocaleDateString('en-US', {
                              month: 'short',
                              day: 'numeric',
                              year: 'numeric',
                            })}
                          </span>
                          <span className="text-sm font-semibold text-red-600">
                            {fmt(balance)} due
                          </span>
                        </div>
                      </div>
                    </label>
                  )
                })}

                {pending.length > 0 && (
                  <div className="flex justify-between items-center px-1">
                    <span className="text-xs text-stone-500">
                      {selectedIds.size} of {pending.length} selected
                    </span>
                    <span className="text-sm font-semibold text-stone-800">
                      Total selected: {fmt(selectedTotal)}
                    </span>
                  </div>
                )}
              </div>

              <div className="border-t border-stone-200 pt-5 space-y-4">
                <p className="text-sm font-semibold text-stone-700">Payment Details</p>

                <div className="flex flex-col gap-1.5">
                  <label className="text-sm font-medium text-stone-700">
                    Payment Amount <span className="text-danger">*</span>
                  </label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400 text-sm">$</span>
                    <input
                      type="text"
                      inputMode="decimal"
                      value={paymentAmount}
                      onChange={e => setPaymentAmount(e.target.value)}
                      disabled={!canRecord}
                      placeholder="0.00"
                      className="flex h-9 w-full rounded border border-stone-300 bg-white pl-7 pr-3 py-2 text-sm text-stone-900 placeholder:text-stone-400 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-brand-500 disabled:bg-stone-50 disabled:text-stone-400"
                    />
                  </div>
                  {selectedTotal > 0 && (
                    <button
                      type="button"
                      onClick={() => setPaymentAmount(selectedTotal.toFixed(2))}
                      className="self-start text-xs text-brand-600 hover:text-brand-700"
                    >
                      Use full balance ({fmt(selectedTotal)})
                    </button>
                  )}
                </div>

                <div className="flex flex-col gap-1.5">
                  <label className="text-sm font-medium text-stone-700">Payment Date</label>
                  <input
                    type="date"
                    value={paymentDate}
                    onChange={e => setPaymentDate(e.target.value)}
                    disabled={!canRecord}
                    className="flex h-9 w-full rounded border border-stone-300 bg-white px-3 py-2 text-sm text-stone-900 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-brand-500 disabled:bg-stone-50 disabled:text-stone-400"
                  />
                </div>

                <div className="flex flex-col gap-1.5">
                  <label className="text-sm font-medium text-stone-700">Payment Method</label>
                  <select
                    value={paymentMethod}
                    onChange={e => setPaymentMethod(e.target.value)}
                    disabled={!canRecord}
                    className="flex h-9 w-full rounded border border-stone-300 bg-white px-3 py-2 text-sm text-stone-900 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-brand-500 disabled:bg-stone-50 disabled:text-stone-400"
                  >
                    <option>Cash</option>
                    <option>Check</option>
                    <option>Bank Transfer</option>
                    <option>Other</option>
                  </select>
                </div>

                <Input
                  label="Reference / Notes"
                  value={notes}
                  onChange={e => setNotes(e.target.value)}
                  placeholder='e.g. Check #1234'
                  disabled={!canRecord}
                />

                {!canRecord && (
                  <p className="text-xs text-stone-400">
                    Only owners and ops can record payments. You can view outstanding balances but not apply payments.
                  </p>
                )}
              </div>
            </div>

            <div className="px-5 py-4 border-t border-stone-200">
              <Button
                className="w-full"
                onClick={handleApply}
                loading={recordPayment.isPending}
                disabled={!canRecord || pending.length === 0}
              >
                Apply Payment
              </Button>
            </div>
          </>
        )}
      </div>
    </>
  )
}

export function BuyersPage() {
  const { data: buyers, isLoading: buyersLoading } = useBuyers()
  const { data: allInvoices, isLoading: invoicesLoading } = useInvoices()
  const [panelBuyer, setPanelBuyer] = useState<Buyer | null | 'new'>(null)
  const [paymentBuyer, setPaymentBuyer] = useState<Buyer | null>(null)

  const isLoading = buyersLoading || invoicesLoading

  const outstandingByBuyer = useMemo(() => {
    const map = new Map<string, number>()
    if (!allInvoices) return map
    for (const inv of allInvoices) {
      if (inv.status === 'paid' || inv.status === 'cancelled') continue
      const balance = computeInvoiceBalance(inv)
      if (balance > 0) {
        map.set(inv.buyer_id, (map.get(inv.buyer_id) ?? 0) + balance)
      }
    }
    return map
  }, [allInvoices])

  if (isLoading) return <PageSpinner />

  const all = buyers ?? []
  const active = all.filter(b => b.is_active).length

  const totalOutstanding = Array.from(outstandingByBuyer.values()).reduce((s, v) => s + v, 0)

  return (
    <div className="p-6 space-y-6">
      <div className="page-header">
        <div>
          <h1 className="text-xl font-semibold text-stone-900">Buyers</h1>
          <p className="text-sm text-stone-500 mt-0.5">
            Wholesale buyers, grocery stores, and distributors
          </p>
        </div>
        <Button size="sm" onClick={() => setPanelBuyer('new')}>
          + Add Buyer
        </Button>
      </div>

      {all.length > 0 && (
        <div className="flex gap-4 flex-wrap">
          <span className="text-sm text-stone-500">
            <span className="font-semibold text-stone-800">{all.length}</span> total
          </span>
          <span className="text-sm text-stone-400">·</span>
          <span className="text-sm text-stone-500">
            <span className="font-semibold text-stone-800">{active}</span> active
          </span>
          {totalOutstanding > 0 && (
            <>
              <span className="text-sm text-stone-400">·</span>
              <span className="text-sm text-red-600">
                <span className="font-semibold">{fmt(totalOutstanding)}</span> outstanding
              </span>
            </>
          )}
        </div>
      )}

      {all.length === 0 ? (
        <div className="card flex flex-col items-center justify-center py-16 text-center">
          <Building2 size={32} className="text-stone-300 mb-3" />
          <p className="text-stone-600 font-medium">No buyers yet</p>
          <p className="text-sm text-stone-400 mt-1 mb-4">
            Add your first wholesale buyer to start creating invoices.
          </p>
          <Button size="sm" onClick={() => setPanelBuyer('new')}>
            + Add First Buyer
          </Button>
        </div>
      ) : (
        <div className="card overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-stone-200 bg-stone-50">
                <th className="text-left px-4 py-3 text-xs font-semibold text-stone-500 uppercase tracking-wide">
                  Prefix
                </th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-stone-500 uppercase tracking-wide">
                  Name
                </th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-stone-500 uppercase tracking-wide">
                  City
                </th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-stone-500 uppercase tracking-wide">
                  Contact
                </th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-stone-500 uppercase tracking-wide">
                  Phone
                </th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-stone-500 uppercase tracking-wide">
                  Status
                </th>
                <th className="text-right px-4 py-3 text-xs font-semibold text-stone-500 uppercase tracking-wide">
                  Outstanding
                </th>
                <th className="px-4 py-3 text-xs font-semibold text-stone-500 uppercase tracking-wide">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {all.map(buyer => {
                const outstanding = outstandingByBuyer.get(buyer.id) ?? 0
                return (
                  <tr
                    key={buyer.id}
                    className="border-b border-stone-100 last:border-0 hover:bg-stone-50 transition-colors cursor-pointer"
                    onClick={() => setPanelBuyer(buyer)}
                  >
                    <td className="px-4 py-3">
                      {buyer.buyer_code ? (
                        <span className="font-mono text-xs font-semibold text-brand-700 bg-brand-50 px-2 py-0.5 rounded tracking-wider">
                          {buyer.buyer_code}{buyer.city_code ?? '??'}
                        </span>
                      ) : (
                        <span className="text-stone-300 text-xs">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <span className="font-semibold text-stone-900">{buyer.name}</span>
                    </td>
                    <td className="px-4 py-3 text-stone-600">
                      {buyer.city ?? <span className="text-stone-300">—</span>}
                    </td>
                    <td className="px-4 py-3 text-stone-600">
                      {buyer.contact_name ?? <span className="text-stone-300">—</span>}
                    </td>
                    <td className="px-4 py-3 text-stone-600">
                      {buyer.phone ?? <span className="text-stone-300">—</span>}
                    </td>
                    <td className="px-4 py-3">
                      {buyer.is_active ? (
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-green-100 text-green-700">
                          Active
                        </span>
                      ) : (
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-stone-100 text-stone-500">
                          Inactive
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right">
                      {outstanding > 0 ? (
                        <span className="font-semibold text-red-600 tabular-nums">
                          {fmt(outstanding)}
                        </span>
                      ) : (
                        <span className="text-stone-300 text-xs">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <div
                        className="flex items-center gap-2"
                        onClick={e => e.stopPropagation()}
                      >
                        <button
                          type="button"
                          onClick={() => setPanelBuyer(buyer)}
                          className="inline-flex items-center gap-1 text-xs text-stone-500 hover:text-stone-700 font-medium"
                        >
                          <FileText size={13} />
                          Edit
                        </button>
                        {outstanding > 0 && (
                          <button
                            type="button"
                            onClick={() => setPaymentBuyer(buyer)}
                            className="inline-flex items-center gap-1 text-xs text-brand-600 hover:text-brand-700 font-medium"
                          >
                            <CreditCard size={13} />
                            Record Payment
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}

      {panelBuyer !== null && (
        <BuyerPanel
          buyer={panelBuyer === 'new' ? null : panelBuyer}
          onClose={() => setPanelBuyer(null)}
        />
      )}

      {paymentBuyer !== null && (
        <RecordPaymentPanel
          buyer={paymentBuyer}
          onClose={() => setPaymentBuyer(null)}
        />
      )}
    </div>
  )
}
